import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";

import { Box, Paper } from "@mui/material";

import { GridRowSelectionModel } from "@mui/x-data-grid";

import AddClientModal from "./AddClientModal";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import {
  Client,
  ClientPreference,
  ClientSettlementType,
  MassiveClient,
} from "types";
import { CLIENTS_URL as BASE_URL } from "constants/urls";

import { ENTITY } from "constants/entities";
import EditClientModal from "./EditClientModal";
import Loader from "../Loader";
import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import { STATUS_CODES } from "constants/index";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { exportToExcel } from "helpers/exportToExcel";
import EditMassiveClientModal from "./EditMassiveClientModal";
import { BiMoney } from "react-icons/bi";

export default function ClientList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [editMassiveOpen, setEditMassiveOpen] = useState<boolean>(false);
  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();
  const {
    loading,
    firstLoad,
    updateData: updateClient,
    deleteData: deleteClient,
    createData: createClient,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Client>(BASE_URL);

  const { updateData: updateMassiveClient, loading: massiveLoading } =
    useCrud<MassiveClient>(BASE_URL);

  const {
    clients,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    cascadeStarts,
    users,
  } = useSession();

  const { showSnackBar } = useToast();

  const enrichedClients = useMemo(() => {
    const clientsMap = new Map(clients.map((client) => [client.id, client]));

    return clients.map((item: Client & { created_by?: number | null }) => {
      const parentClient = item.parent_client_id
        ? clientsMap.get(item.parent_client_id)
        : null;

      return {
        ...item,
        created_by_name: item.created_by
          ? getUserFullName(item.created_by, users)
          : "SISTEMA",
        parent_client_name: parentClient
          ? `${parentClient.code} - ${parentClient.razon_social}`
          : "-",
      };
    });
  }, [clients, users]);

  const {
    filterText,
    setFilterText,
    filteredData: filteredClients,
  } = useFilter<Client & { created_by_name: string; parent_client_name: string }>({
    data: enrichedClients,
    fields: gridStructure[ENTITY.CLIENTS].fields,
  });

  const clientExcelColumns = {
    Código: (c: Client) => c.code ?? "-",
    "Razón social": (c: Client) => c.razon_social ?? "-",
    Dirección: (c: Client) => c.address ?? "-",
    CUIT: (c: Client) => c.cuit ?? "-",
    Contacto: (c: Client) => c.contact ?? "-",
    Observaciones: (c: Client) => c.observations ?? "-",
    Activo: (c: Client) => (c.is_active ? "SI" : "NO"),
    "Cliente Padre": (
      c: Client & { parent_client_name?: string }
    ) => c.parent_client_name ?? "-",
    "Creado por": (c: Client & { created_by_name?: string }) =>
      c.created_by_name ?? "-",
  } satisfies Record<string, (row: Client) => any>;

  const handleExportClientsExcel = () => {
    exportToExcel<Client>({
      data: filteredClients,
      columns: clientExcelColumns,
      sheetName: "Clientes",
      fileNamePrefix: "Clientes",
      headerRows: [
        ["Listado de Clientes"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const handleCreate = async (newClient: Client) => {
    const response = await createClient(newClient, true, true);

    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.CLIENTS, {
        ...newClient,
        ...(response.data as Client),
      });
    }
  };

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteClient(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.CLIENTS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    setSelected([]);
  };

  const handleEdit = async (clientToEdit: Client) => {
    const response = await updateClient(
      clientToEdit.id,
      clientToEdit,
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.CLIENTS, clientToEdit);
    }
    setSelected([]);
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const handleMassiveEdit = async (preferencesToEdit: ClientPreference[]) => {
    const response = await updateMassiveClient(
      "massive",
      {
        id: selected.map((s) => parseInt(s.toString())),
        client_settlement_types: preferencesToEdit
          .filter((p) => p.price > 0)
          .map((p) => ({
            id: p.settlement_type_id,
            type: p.movement_type,
            price: p.price,
          })),
      },
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      const clientSettlementTypes = response.data as ClientSettlementType[];
      for (const id of selected) {
        const existingClientSettlementTypes = clientSettlementTypes.filter(
          (cst) => cst.client_id === id,
        );
        if (existingClientSettlementTypes.length > 0) {
          const existingClient = clients.find((c) => c.id === id);
          if (existingClient) {
            updateSessionData(ENTITY.CLIENTS, {
              ...existingClient,
              client_settlement_types:
                existingClient.client_settlement_types?.map((cst) => {
                  const existingClientSettlementType =
                    existingClientSettlementTypes.find(
                      (ecst) =>
                        ecst.settlement_type_id === cst.settlement_type_id &&
                        ecst.movement_type === cst.movement_type,
                    );
                  return existingClientSettlementType || cst;
                }),
            });
          }
        }
      }
      showSnackBar("Se han actualizado los datos correctamente.", "success");
    } else {
      showSnackBar(
        "Ha ocurrido un error al querer actualizar los datos.",
        "error",
      );
    }
    setSelected([]);
  };

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader
            isFirstLoad={firstLoad}
            isLoading={loading || massiveLoading}
          />
          <AddClientModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
            cascadeStarts={cascadeStarts}
            clients={clients}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.CLIENTS}
            values={selected.map((item) => {
              const value = clients.find((c) => c.id === item)?.razon_social;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditClientModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={clients.find((c) => c.id === selected[0]) as Client}
              onEdit={handleEdit}
              cascadeStarts={cascadeStarts}
              clients={clients}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                massiveEditIcon={<BiMoney />}
                handleMassiveEditModalOpen={() =>
                  setEditMassiveOpen(!editMassiveOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportClientsExcel}
              />
              <CustomGrid
                structure={gridStructure[ENTITY.CLIENTS]}
                rows={filteredClients}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
      <EditMassiveClientModal
        open={editMassiveOpen}
        handleOpen={() => setEditMassiveOpen(!editMassiveOpen)}
        onEdit={handleMassiveEdit}
      />
    </>
  );
}
